import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { asPlaceholder } from "../../i18n/filterLabels";
import GetData from "../../services/getData";
import { useUserSettings } from "../../context/UserSettingsContext";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import { Col, Container, Row } from "react-bootstrap";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import { Button } from "../../components/ui";
import DateInput from "../../components/ui/DateInput";
import scheduleIcon from "../../media/icons8-appointments.png";
import filterIcon from "../../media/filter_icon.png";
import "./schedule.css";

const SLOT_MINUTES = 15;
const DAY_START_HOUR = 8;
const DAY_END_HOUR = 20;

const DOCTOR_COL_MIN = 100;
const DOCTOR_COL_MAX = 200;

function timeSlots() {
    const slots = [];
    for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) {
        for (let m = 0; m < 60; m += SLOT_MINUTES) {
            if (h === DAY_END_HOUR && m > 0) break;
            slots.push(
                `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
            );
        }
    }
    return slots;
}

/** Показывать в шкале только целые часы (10:00, 11:00, ...) */
function formatTimeLabel(slot) {
    if (!slot) return "";
    const [h, m] = slot.split(":").map(Number);
    return m === 0 ? `${String(h).padStart(2, "0")}:00` : "";
}

function getSlotKey(datetime) {
    if (!datetime) return null;
    const d = new Date(datetime);
    const totalMin = d.getHours() * 60 + d.getMinutes();
    const slotMin = Math.floor(totalMin / SLOT_MINUTES) * SLOT_MINUTES;
    const h = Math.floor(slotMin / 60);
    const m = slotMin % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Один специалист: { id, name } */
function getStaffName(staff) {
    return staff?.name || staff?.nickname || "—";
}

/**
 * Список всех специалистов, привязанных к записи.
 * Поддержка: staff_list (массив), один staff, или staff_id.
 * Одна запись возвращается для каждого врача, чтобы отображаться во всех столбцах.
 */
function getStaffList(rec) {
    const list = [];
    if (Array.isArray(rec.staff_list) && rec.staff_list.length > 0) {
        rec.staff_list.forEach((s) => {
            const id = s?.id ?? s;
            if (id != null) list.push({ id, name: getStaffName(s) });
        });
    }
    if (list.length > 0) return list;
    const single = rec.staff;
    if (single && (single.id != null || single.id === 0)) {
        return [{ id: single.id, name: getStaffName(single) }];
    }
    const sid = rec.staff_id;
    if (sid != null) {
        return [{ id: sid, name: getStaffName(rec.staff) || "—" }];
    }
    return [];
}

/** Длительность в API в секундах. Возвращает число слотов по 30 мин. */
function getRecordSpan(rec) {
    const durationSec = rec.seance_length ?? rec.length ?? SLOT_MINUTES * 60;
    const durationMin = durationSec / 60;
    return Math.max(1, Math.ceil(durationMin / SLOT_MINUTES));
}

const ROW_HEIGHT = 20;
const TIME_COL_WIDTH = 72; /* колонка «Время» на 20px шире */
const TIME_HEADER_HEIGHT = 40;
const DOCTOR_HEADER_TOP = 0;
const DOCTOR_HEADER_HEIGHT = 60;
const HEADER_HEIGHT = DOCTOR_HEADER_TOP + DOCTOR_HEADER_HEIGHT; /* начало строк сетки */
const SKELETON_DOCTOR_COUNT = 5;
const SKELETON_COLUMN_BLOCKS = [
    [
        { slotIdx: 4, span: 4 },
        { slotIdx: 14, span: 3 },
        { slotIdx: 24, span: 5 },
        { slotIdx: 36, span: 3 },
    ],
    [
        { slotIdx: 2, span: 3 },
        { slotIdx: 10, span: 6 },
        { slotIdx: 22, span: 2 },
        { slotIdx: 32, span: 4 },
    ],
    [
        { slotIdx: 6, span: 5 },
        { slotIdx: 16, span: 2 },
        { slotIdx: 26, span: 4 },
        { slotIdx: 38, span: 3 },
    ],
    [
        { slotIdx: 1, span: 2 },
        { slotIdx: 8, span: 4 },
        { slotIdx: 18, span: 7 },
        { slotIdx: 34, span: 2 },
    ],
    [
        { slotIdx: 5, span: 3 },
        { slotIdx: 12, span: 5 },
        { slotIdx: 28, span: 3 },
        { slotIdx: 40, span: 4 },
    ],
];

function getSkeletonPlacements(doctorCount) {
    const placements = [];
    for (let docIdx = 0; docIdx < doctorCount; docIdx++) {
        const blocks = SKELETON_COLUMN_BLOCKS[docIdx % SKELETON_COLUMN_BLOCKS.length];
        blocks.forEach((block) => {
            placements.push({
                slotIdx: block.slotIdx,
                docIdx,
                span: block.span,
                rowOffset: 0,
            });
        });
    }
    return placements;
}

/** Слот (дата + время) уже в прошлом относительно текущего момента */
function isSlotPast(dateStr, slotStr) {
    if (!dateStr || !slotStr) return false;
    const slotDate = new Date(dateStr + "T" + slotStr + ":00");
    return slotDate < new Date();
}

function ScheduleGrid({ records, meta, date, onRecordClick, loading = false }) {
    const { t } = useTranslation("schedule");
    const wrapRef = React.useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const { width } = entries[0]?.contentRect ?? {};
            if (typeof width === "number") setContainerWidth(width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const { slots, doctors, placements, maxPlacementEndRow } = useMemo(() => {
        const slotList = timeSlots();
        const slotKeyToIndex = {};
        slotList.forEach((s, i) => {
            slotKeyToIndex[s] = i;
        });

        const doctorSet = new Map();
        records.forEach((rec) => {
            getStaffList(rec).forEach((s) => {
                if (s.id != null && !doctorSet.has(s.id)) {
                    doctorSet.set(s.id, { id: s.id, name: s.name });
                }
            });
        });
        const doctorList = Array.from(doctorSet.values()).sort((a, b) =>
            a.name.localeCompare(b.name)
        );

        if (loading) {
            const skeletonDoctors =
                doctorList.length > 0
                    ? doctorList
                    : Array.from({ length: SKELETON_DOCTOR_COUNT }, (_, i) => ({
                          id: `skeleton-${i}`,
                          name: "",
                      }));
            return {
                slots: slotList,
                doctors: skeletonDoctors,
                placements: getSkeletonPlacements(skeletonDoctors.length),
                maxPlacementEndRow: slotList.length,
            };
        }

        const doctorIdToIndex = {};
        doctorList.forEach((d, i) => {
            doctorIdToIndex[d.id] = i;
        });

        const byCell = {};
        records.forEach((rec) => {
            const slot = getSlotKey(rec.datetime || rec.date);
            if (slot == null) return;
            const slotIdx = slotKeyToIndex[slot];
            if (slotIdx == null) return;
            getStaffList(rec).forEach((s) => {
                const docIdx = doctorIdToIndex[s.id];
                if (docIdx == null) return;
                const key = `${slotIdx}_${docIdx}`;
                if (!byCell[key]) byCell[key] = [];
                byCell[key].push(rec);
            });
        });

        const placementsList = [];
        Object.entries(byCell).forEach(([key, recs]) => {
            const [slotIdx, docIdx] = key.split("_").map(Number);
            let rowOffset = 0;
            recs.forEach((rec) => {
                const span = getRecordSpan(rec);
                placementsList.push({
                    rec,
                    slotIdx,
                    docIdx,
                    span,
                    rowOffset,
                });
                rowOffset += span;
            });
        });

        const maxEndRow = placementsList.reduce((max, p) => {
            const endRow = (p.slotIdx ?? 0) + (p.rowOffset ?? 0) + (p.span ?? 1);
            return Math.max(max, endRow);
        }, slotList.length);

        return {
            slots: slotList,
            doctors: doctorList,
            placements: placementsList,
            maxPlacementEndRow: maxEndRow,
        };
    }, [records, loading]);

    const attendanceMap = {
        "-1": "noShow",
        0: "waiting",
        1: "served",
        2: "confirmed",
        3: "refused",
    };

    const doctorCount = Math.max(1, doctors.length);
    const availableForCols = Math.max(0, containerWidth - TIME_COL_WIDTH);
    const colWidth = containerWidth > 0
        ? Math.min(DOCTOR_COL_MAX, Math.max(DOCTOR_COL_MIN, Math.floor(availableForCols / doctorCount)))
        : DOCTOR_COL_MAX;
    const totalWidth = TIME_COL_WIDTH + doctors.length * colWidth;
    const GRID_BOTTOM_PADDING = 48;
    const gridRows = Math.max(slots.length, maxPlacementEndRow || 0);
    const totalHeight = HEADER_HEIGHT + gridRows * ROW_HEIGHT + GRID_BOTTOM_PADDING;

    return (
        <>
            {/*meta && (
                <div className="schedule-meta">
                    Записей: {meta.total_count ?? records.length}
                </div>
            )*/}
            <div
                ref={wrapRef}
                className={`schedule-records schedule-grid-wrap${loading ? " schedule-grid-wrap--loading" : ""}`}
                aria-busy={loading}
            >
                <div
                    className={`schedule-absolute-grid${loading ? " schedule-absolute-grid--loading" : ""}`}
                    style={{
                        width: totalWidth,
                        height: totalHeight,
                        minWidth: "100%",
                        paddingBottom: GRID_BOTTOM_PADDING,
                    }}
                >
                    {/* Заголовок: время */}
                    <div
                        className="schedule-grid-time schedule-grid-header-time"
                        style={{
                            left: 0,
                            top: 0,
                            width: TIME_COL_WIDTH,
                            height: TIME_HEADER_HEIGHT,
                        }}
                    >
                        {t("time")}
                    </div>
                    {/* Заголовки: доктора */}
                    {doctors.map((doc, docIdx) => (
                        <div
                            key={doc.id}
                            className="schedule-grid-doctor schedule-grid-header-doctor"
                            style={{
                                left: TIME_COL_WIDTH + docIdx * colWidth,
                                top: DOCTOR_HEADER_TOP,
                                width: colWidth,
                                height: DOCTOR_HEADER_HEIGHT,
                                "--skeleton-delay": `${docIdx * 0.12}s`,
                            }}
                        >
                            {loading ? (
                                <span className="schedule-skeleton-line" />
                            ) : (
                                doc.name
                            )}
                        </div>
                    ))}
                    {/* Строки времени слева — только целые часы в подписи */}
                    {slots.map((slot, rowIdx) => {
                        const past = isSlotPast(date, slot);
                        return (
                            <div
                                key={slot}
                                className={`schedule-grid-time schedule-grid-row-time${past ? " schedule-grid-row-time--past" : ""}`}
                                style={{
                                    left: 0,
                                    top: HEADER_HEIGHT + rowIdx * ROW_HEIGHT,
                                    width: TIME_COL_WIDTH,
                                    height: ROW_HEIGHT,
                                }}
                            >
                                {formatTimeLabel(slot)}
                            </div>
                        );
                    })}
                    {/* Фон ячеек (сетка) */}
                    {slots.map((slot, rowIdx) => {
                        const past = isSlotPast(date, slot);
                        return doctors.map((doc, docIdx) => (
                            <div
                                key={`${rowIdx}-${doc.id}`}
                                className={`schedule-grid-cell-bg${docIdx === 0 ? " schedule-grid-cell-bg-first" : ""}${past ? " schedule-grid-cell-bg--past" : ""}`}
                                style={{
                                    left: TIME_COL_WIDTH + docIdx * colWidth,
                                    top: HEADER_HEIGHT + rowIdx * ROW_HEIGHT,
                                    width: colWidth,
                                    height: ROW_HEIGHT,
                                }}
                            />
                        ));
                    })}
                    {/* Блоки записей / скелетон загрузки */}
                    {loading
                        ? placements.map(({ slotIdx, docIdx, span, rowOffset }) => (
                            <div
                                key={`skeleton-${docIdx}-${slotIdx}-${rowOffset}`}
                                className="schedule-grid-card-absolute schedule-skeleton-card"
                                style={{
                                    left: TIME_COL_WIDTH + docIdx * colWidth + 2,
                                    top:
                                        HEADER_HEIGHT +
                                        slotIdx * ROW_HEIGHT +
                                        rowOffset * ROW_HEIGHT +
                                        2,
                                    width: colWidth - 4,
                                    height: span * ROW_HEIGHT - 4,
                                    "--skeleton-delay": `${docIdx * 0.12}s`,
                                }}
                            >
                                <span className="schedule-skeleton-line schedule-skeleton-line--title" />
                                {span >= 3 && (
                                    <span className="schedule-skeleton-line schedule-skeleton-line--text" />
                                )}
                            </div>
                        ))
                        : placements.map(({ rec, slotIdx, docIdx, span, rowOffset }) => {
                        const client = rec.client || rec.comer || {};
                        const idYc = client.id != null ? String(client.id) : null;
                        const name =
                            client.name || client.fullname || "—";
                        const services = (rec.services || [])
                            .map((s) => s.title || s.name || "")
                            .filter(Boolean)
                            .join(", ") || "—";
                        const lengthSec =
                            rec.seance_length ?? rec.length ?? 0;
                        const lengthMin = lengthSec > 0 ? Math.round(lengthSec / 60) : 0;
                        const attendanceKey = String(
                            rec.attendance ?? rec.visit_attendance
                        );
                        const statusKey = attendanceMap[attendanceKey] ?? null;
                        const statusLabel = statusKey
                            ? t(`attendance.${statusKey}`)
                            : "—";
                        const isNoShowOrDone =
                            statusKey === "noShow" || statusKey === "served";
                        const isDone = statusKey === "served";
                        const isRefused = statusKey === "refused";
                        const isNoShow = statusKey === "noShow";
                        const isConfirmed = statusKey === "confirmed";
                        const cardClasses = [
                            "schedule-grid-card",
                            "schedule-grid-card-absolute",
                            idYc ? "schedule-grid-card-clickable" : "",
                            isNoShowOrDone ? "schedule-grid-card--muted" : "",
                            isDone ? "schedule-grid-card--done" : "",
                            (isRefused || isNoShow) ? "schedule-grid-card--refused" : "",
                            isConfirmed ? "schedule-grid-card--confirmed" : "",
                        ]
                            .filter(Boolean)
                            .join(" ");
                        const handleClick = () => {
                            if (idYc && onRecordClick) onRecordClick({ idYc, name });
                        };
                        return (
                            <div
                                key={`${rec.id}-${slotIdx}-${docIdx}-${rowOffset}`}
                                className={cardClasses}
                                title={`${name} • ${services}${lengthMin ? ` • ${lengthMin} ${t("minutes")}` : ""} • ${statusLabel}`}
                                style={{
                                    left: TIME_COL_WIDTH + docIdx * colWidth + 2,
                                    top:
                                        HEADER_HEIGHT +
                                        slotIdx * ROW_HEIGHT +
                                        rowOffset * ROW_HEIGHT +
                                        2,
                                    width: colWidth - 4,
                                    height: span * ROW_HEIGHT - 4,
                                }}
                                onClick={handleClick}
                                onKeyDown={(e) => { if (idYc && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); handleClick(); } }}
                                role={idYc ? "button" : undefined}
                                tabIndex={idYc ? 0 : undefined}
                            >
                                <div className="schedule-grid-card-name">
                                    {name}
                                </div>
                                {services !== "—" && (
                                    <div className="schedule-grid-card-services">
                                        {services}
                                    </div>
                                )}
                                {lengthMin > 0 && (
                                    <span className="schedule-grid-card-length">
                                        {lengthMin} {t("minutes")}
                                    </span>
                                )}
                                <span className="schedule-grid-card-status">
                                    {statusLabel}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

function Schedule() {
    const { t } = useTranslation("schedule");
    const navigate = useNavigate();
    const { get, update } = useUserSettings();
    const [date, setDate] = useState(() => {
        const d = new Date();
        return d.toISOString().slice(0, 10);
    });
    const [branch, setBranch] = useState(() =>
        String(get("filters.schedule.clinic_id", "") || "")
    );
    const [staffId, setStaffId] = useState(() =>
        String(get("filters.schedule.staff_id", "") || "")
    );
    const [clinics, setClinics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFiltersActive, setIsFiltersActive] = useState(true);
    const [records, setRecords] = useState([]);
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState(null);

    // Дата по умолчанию — сегодняшний день, это не фильтр, а точка отсчёта.
    // Подсвечиваем поле, только если пользователь выбрал другой день.
    const todayIso = new Date().toISOString().slice(0, 10);
    const hasActiveFilters = branch !== "" || date !== todayIso;

    const handleRecordClick = useCallback(({ idYc, name }) => {
        navigate(`/patient_yc/${encodeURIComponent(idYc)}`, {
            state: { fio: name, clinic_id: branch || "" },
        });
    }, [navigate, branch]);

    const loadClinics = useCallback(async () => {
        try {
            const res = await GetData({
                module: "schedule",
                block: "list",
                action: "get_clinics",
            });
            if (res && res.success && Array.isArray(res.data)) {
                setClinics(res.data);
            }
        } catch (err) {
            console.error("Ошибка загрузки списка филиалов:", err);
        }
    }, []);

    useEffect(() => {
        loadClinics();
    }, [loadClinics]);

    const loadSchedule = useCallback(async () => {
        setError(null);
        setLoading(true);
        try {
            const res = await GetData({
                module: "schedule",
                block: "list",
                action: "get",
                date: date || undefined,
                clinic_id: branch || undefined,
                staff_id: staffId ? parseInt(staffId, 10) : undefined,
            });
            if (res && res.success) {
                setRecords(Array.isArray(res.data) ? res.data : []);
                setMeta(res.meta || null);
            } else {
                setRecords([]);
                setMeta(null);
                setError(res?.message || t("errors.loadFailed"));
            }
        } catch (err) {
            console.error("Ошибка загрузки расписания:", err);
            setRecords([]);
            setError(t("errors.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [date, branch, staffId, t]);

    useEffect(() => {
        document.title = t("title");
        const handler = setTimeout(() => {
            loadSchedule();
        }, 400);
        return () => clearTimeout(handler);
    }, [loadSchedule, t]);

    return (
        <Container fluid="md">
            <Row>
                <Col>
                    <BlockTitle
                        title={
                            <TitleWithIcon
                                icon={scheduleIcon}
                                title={t("title")}
                                showStatus={false}
                                status=""
                            />
                        }
                        buttons={[]}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <div className="page schedule">
                        <div className="page-content">
                            {/* Панель фильтров */}
                            <div className="page-block">
                                <div
                                    className={`filters-panel ${
                                        isFiltersActive ? "active" : ""
                                    } ${hasActiveFilters ? "has-active-filters" : ""}`}
                                >
                                    <div className="filters-panel-title">
                                        <div className="filters-panel-title-icon">
                                            <img
                                                className="title-with-icon__image"
                                                src={filterIcon}
                                                alt={t("filters.alt", { ns: "common" })}
                                                title={t("filters.alt", { ns: "common" })}
                                            />
                                            <div className="filters-panel-name">
                                                {t("filters.title", { ns: "common" })}
                                            </div>
                                        </div>
                                        <Button
                                            onClick={() =>
                                                setIsFiltersActive((prev) => !prev)
                                            }
                                            size="sm"
                                            variant="secondary">
                                            {isFiltersActive
                                                ? t("filters.hide", { ns: "common" })
                                                : t("filters.show", { ns: "common" })}
                                        </Button>
                                    </div>
                                    <div className="filters-section">
                                        <div
                                            className={`filter-group ${
                                                date !== todayIso ? "is-filter-active" : ""
                                            }`}
                                        >
                                            <DateInput
                                                value={date}
                                                hint={asPlaceholder(t("dateLabel"))}
                                                onChange={(e) =>
                                                    setDate(e.target.value)
                                                }
                                                className="search-input"
                                            />
                                        </div>
                                        <div
                                            className={`filter-group schedule-filter-group ${
                                                branch !== "" ? "is-filter-active" : ""
                                            }`}
                                        >
                                            <select
                                                className={`search-input ${
                                                    branch === ""
                                                        ? "is-placeholder"
                                                        : ""
                                                }`}
                                                value={branch}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setBranch(value);
                                                    // запоминаем выбор филиала в настройках пользователя
                                                    update({ filters: { schedule: { clinic_id: value } } });
                                                }}
                                            >
                                                <option value="">
                                                    {asPlaceholder(t("branchLabel"))}
                                                </option>
                                                {clinics
                                                    .filter(
                                                        (c) =>
                                                            (c.id_yc != null && c.id_yc !== "") ||
                                                            (c.id_al != null && c.id_al !== "")
                                                    )
                                                    .map((c) => (
                                                        <option
                                                            key={c.id}
                                                            value={String(c.id)}
                                                        >
                                                            {c.name || t("branchFallback", { id: c.id })}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                        <Button
                                            disabled={loading}
                                            onClick={loadSchedule}
                                            size="sm"
                                            variant="secondary">
                                            {loading ? t("loading") : t("refresh")}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Список записей YClients */}
                            <div className="page-block table-list schedule-block">
                                {loading ? (
                                    <ScheduleGrid
                                        records={records}
                                        meta={meta}
                                        date={date}
                                        onRecordClick={handleRecordClick}
                                        loading
                                    />
                                ) : error ? (
                                    <div className="no-patients">{error}</div>
                                ) : records.length === 0 ? (
                                    <div className="no-patients">
                                        {t("noRecords")}
                                    </div>
                                ) : (
                                    <ScheduleGrid records={records} meta={meta} date={date} onRecordClick={handleRecordClick} />
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export default Schedule;
